/**
 * A paragraph of the privacy page with one link inside its text: the message holds `{link}label{/link}` where the link
 * goes (Matt GO 2026-10-23, option a: the hosting text names the browser's network error report and links to Cloudflare's
 * explanation). The URL is in outboundLinks.json; the link opens in a new tab and sends no referrer, and nothing is fetched.
 */
import OUTBOUND from './outboundLinks.json';

type El = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Record<string, string>, ...children: (Node | string)[]) => HTMLElementTagNameMap[K];

/** The text split around its `{link}…{/link}`, or undefined when it has none. */
export function splitLink(text: string): { before: string; label: string; after: string } | undefined {
  const m = /^([\s\S]*?)\{link\}([\s\S]*?)\{\/link\}([\s\S]*)$/.exec(text);
  return m ? { before: m[1] ?? '', label: m[2] ?? '', after: m[3] ?? '' } : undefined;
}

/** Which message holds a link, and where it goes. */
const LINKED: Record<string, string> = { 'page.privacy.hostingReport': OUTBOUND.cloudflareNel };

/** The children of a paragraph for message `key` (its text, with the link drawn where the message puts it). */
export function paragraphChildren(el: El, key: string, text: string, newTab: string): (Node | string)[] {
  const href = LINKED[key];
  const parts = href ? splitLink(text) : undefined;
  if (!href || !parts) return [text];
  return [parts.before, el('a', { href, target: '_blank', rel: 'noopener noreferrer', class: 'nowrap-link' }, parts.label, el('span', { class: 'visually-hidden' }, ` (${newTab})`)), parts.after];
}
